import { useState } from 'react';
import toast from 'react-hot-toast';

import { Copy, CopyFilled } from '@/resources/assets/images';

const MILLISECONDS_TO_FADE = 1.5 * 1000;

type CopyToClipboardProps = {
	textToCopy: string;
	type?: string;
};

const useCopyFunction = () => {
	if (navigator.clipboard && navigator.clipboard.writeText) {
		return {
			copy: (textToCopy: string) =>
				navigator.clipboard.writeText(textToCopy)
		};
	}

	return {
		copy: async (textToCopy: string) => {
			const textarea = document.createElement('textarea');
			textarea.value = textToCopy;
			document.body.appendChild(textarea);
			textarea.select();

			return new Promise<void>((resolve, reject) => {
				try {
					document.execCommand('copy');
				} catch (err) {
					reject(err);
				} finally {
					document.body.removeChild(textarea);
					resolve();
				}
			});
		}
	};
};

export default function CopyToClipboard({
	textToCopy,
	type
}: CopyToClipboardProps) {
	const [clicked, setClicked] = useState(false);

	const { copy } = useCopyFunction();

	const handleCopy = async () => {
		setClicked(true);

		try {
			await copy(textToCopy);

			toast.success(
				type
					? `${type} has been successfully copied to the clipboard`
					: 'Copied to clipboard successfully'
			);
		} catch (error) {
			toast.error('Failed to copy text to clipboard');

			throw new Error(`Failed to copy text: ${error}`);
		}

		setTimeout(() => {
			setClicked(false);
		}, MILLISECONDS_TO_FADE);
	};

	const CopyIcon = clicked ? CopyFilled : Copy;

	return (
		<div
			data-is-clicked={clicked}
			className="group flex items-center gap-1"
		>
			<button
				type="button"
				onClick={handleCopy}
				data-is-clicked={clicked}
			>
				<CopyIcon className="transition-colors group-hover:[&_path]:fill-dm-gold" />
			</button>

			<span
				data-is-clicked={clicked}
				className="text-xs font-bold text-orange opacity-0 transition-opacity group-hover:block group-hover:opacity-100"
			>
				Copy to clipboard
			</span>
		</div>
	);
}
